import React from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import apiClient from "../../../../application/apis/api-client";
import { OnlineInfo } from "../../../../application/domain/proc-management";
import {
  isNotFoundError,
  isPermissionError,
  reportProcManagementRelatedError,
} from "../../../../application/domain/proc-management/util/error";

type OptionType = {
  label: string;
  value: string;
  onlineInfo?: OnlineInfo;
};

export const ProcMasterModal: React.FC<{
  orgId: string;
  groupIds?: string[];
  onClose: () => void;
  onUpdateProcMaster: (procMstId: string) => Promise<void>;
}> = ({ orgId, groupIds, onClose, onUpdateProcMaster }) => {
  const [name, setName] = React.useState<string>("");
  const [bouncedName] = useDebounce(name, 500);
  const [currentProc, setCurrentProc] = React.useState<OptionType>();
  const [options, setOptions] = React.useState<OptionType[]>([]);
  const [isDisplayWarning, setIsDisplayWarning] = React.useState(false);
  React.useEffect(() => {
    const fetchProcMaster = async () => {
      const fetcher =
        bouncedName !== ""
          ? () => {
              return apiClient.procManagement.searchProcs({
                organizationId: orgId,
                groupIds: groupIds,
                query: bouncedName,
              });
            }
          : () => {
              return apiClient.procManagement.listProcs({
                organizationId: orgId,
                groupIds: groupIds,
              });
            };
      try {
        const { data } = await fetcher();
        const procs = "hits" in data ? data.hits : data.items;
        setOptions(
          procs
            .filter((p) => {
              return (
                p.contentStatus === "DRAFT" ||
                (p.contentStatus === "PUBLISHED" && p.acceptAppOnline)
              );
            })
            .map((p) => {
              return {
                label: p.name,
                value: p.id,
                onlineInfo: p.onlineInfo,
              };
            }),
        );
      } catch (e) {
        reportProcManagementRelatedError(e);
        if (isNotFoundError(e)) {
          toast.error(`手続きマスタの取得に失敗しました。`);
        } else if (isPermissionError(e)) {
          toast.error(`手続きマスタを表示する権限がありません。`);
        } else {
          toast.error(
            `手続きマスタ一覧の取得に失敗しました。電波状況のよい場所でリロードしてください。`,
          );
        }
      }
    };
    fetchProcMaster();
  }, [bouncedName, orgId, groupIds, setOptions]);

  const handleSubmit = React.useCallback(() => {
    if (currentProc) {
      onUpdateProcMaster(currentProc.value).then(onClose);
    }
  }, [currentProc, onClose, onUpdateProcMaster]);

  const decideWarning = (info?: OnlineInfo): boolean => {
    return (
      !!info &&
      ((!!info.grafferSmartApplyInfo &&
        info.grafferSmartApplyInfo.procedureId !== "") ||
        (!!info.applicationUrl && info.applicationUrl !== ""))
    );
  };

  return (
    <form>
      <section className="modal-card-body">
        <label htmlFor="name" className="label">
          手続きマスタ
          <p className="help has-text-weight-normal">
            この手続きの申請開始ページのリンクが、選択された手続きマスタに掲載されます。
            <br />
            電子申請を受け付けていない公開済の手続きマスタは除外されています。
          </p>
        </label>
        <Select
          placeholder={"選択してください"}
          value={currentProc}
          onInputChange={setName}
          onChange={(opt) => {
            if (opt) {
              setCurrentProc({ label: opt.label, value: opt.value });
              setIsDisplayWarning(decideWarning(opt.onlineInfo));
            }
          }}
          options={options}
        />
        {isDisplayWarning && (
          <p className="help  has-text-warning-dark">
            この手続きマスタには既に別の申請開始ページのリンクが掲載されています。
            <br />
            この操作によって掲載されるリンクが上書きされますのでご注意ください。
          </p>
        )}
      </section>
      <section className="modal-card-foot">
        <button
          type="button"
          className="button is-failure is-fullwidth"
          onClick={onClose}
        >
          閉じる
        </button>
        <button
          type="button"
          className="button is-info is-fullwidth"
          onClick={handleSubmit}
          disabled={!currentProc}
        >
          選択する
        </button>
      </section>
    </form>
  );
};
